import type { ChartV2Point, ChartV2Series } from '@chartv2';

export interface SeriesColumn {
  label: string;
  valuesByTime: Map<number, number>;
}

export interface ChartTable {
  columns: SeriesColumn[];
  times: number[];
  categories: string[];
}

// Excel caps worksheet (tab) names at 31 characters.
const SHEET_NAME_MAX_LENGTH = 31;

const pad = (value: number): string => String(value).padStart(2, '0');

const getPointValue = (point: ChartV2Point): number =>
  'value' in point ? point.value : point.close;

const hasIntradayTimes = (series: ChartV2Series[]): boolean =>
  series.some(item =>
    item.data.some(point => {
      const date = new Date(point.time);

      return date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0;
    })
  );

const hasSecondPrecision = (series: ChartV2Series[]): boolean =>
  series.some(item =>
    item.data.some(point => {
      const date = new Date(point.time);

      return date.getUTCSeconds() !== 0 || date.getUTCMilliseconds() !== 0;
    })
  );

const formatDateLabel = (
  timeMs: number,
  includeTime: boolean,
  includeSeconds: boolean
): string => {
  const date = new Date(timeMs);
  const day = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate()
  )}`;

  if (!includeTime) {
    return day;
  }

  const time = `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}${
    includeSeconds ? `:${pad(date.getUTCSeconds())}` : ''
  }`;

  return `${day} ${time}`;
};

/** Excel sheet names cannot exceed 31 chars or contain `\ / ? * [ ] :`. */
export const sanitizeSheetName = (title: string): string => {
  const cleaned = title
    .replace(/[\\/?*[\]:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned.slice(0, SHEET_NAME_MAX_LENGTH).trim() || 'Chart';
};

export const hasExportableData = (series: ChartV2Series[]): boolean =>
  series.some(item => item.data.length > 0);

/**
 * Pivots the chart series into a tabular shape: a sorted union of all
 * timestamps (rows) and one value column per series. OHLC series contribute
 * their close value so they can sit alongside line series.
 */
export const buildChartTable = (series: ChartV2Series[]): ChartTable => {
  const includeTime = hasIntradayTimes(series);
  const includeSeconds = includeTime && hasSecondPrecision(series);

  const columns: SeriesColumn[] = series.map(item => ({
    label: item.label,
    valuesByTime: new Map(
      item.data.map(point => [point.time, getPointValue(point)])
    ),
  }));

  const times = Array.from(
    new Set(series.flatMap(item => item.data.map(point => point.time)))
  ).sort((a, b) => a - b);

  const categories = times.map(time =>
    formatDateLabel(time, includeTime, includeSeconds)
  );

  return { columns, times, categories };
};
